import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseTableOptionsDropdown from 'components/atoms/base-table-option-dropdown';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import type { SortState } from 'infrastructure/types';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  ICondition,
  IConditionForUpdate,
  IConditionIcdCode,
} from 'infrastructure/interfaces';

interface Props {
  setConditionForUpdate: (args: IConditionForUpdate) => void;
  toggleOpen: () => void;
  handleDelete: (guid: string) => void;
  loading: boolean;
  updatedItemGuid?: string;
  disableActions?: boolean;
  documentationTimeSort: SortState;
}

export const columns = ({
  setConditionForUpdate,
  toggleOpen,
  handleDelete,
  loading,
  updatedItemGuid,
  disableActions,
  documentationTimeSort,
}: Props): IBaseTableColumns<ICondition> => {
  return [
    {
      label: i18next.t('labels.dateTime'),
      key: 'createdAt',
      width: '120px',
      render(date: string) {
        return DateFormatter({
          date,
          format: DateFormats['MM-dd-yyyy h:mm a'],
        });
      },
      dataCy: 'conditions-goal-date',
    },
    {
      label: i18next.t('labels.documentationTime'),
      key: 'documentationTime',
      align: 'center',
      width: '120px',
      render(documentationTime?: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return (
          <span data-cy="conditions-goal-documentation-time">{actionTime}</span>
        );
      },
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t('labels.icdCode'),
      key: 'icdCode',
      width: '120px',
      render(icdCode: IConditionIcdCode) {
        return icdCode.code;
      },
      dataCy: 'conditions-goal-icd-code',
    },
    {
      label: i18next.t('labels.description'),
      key: 'description',
      width: '700px',
      responsive: ['lg'],
      dataCy: 'conditions-goal-description',
    },
    {
      label: i18next.t('labels.option'),
      width: '80px',
      align: 'center',
      key: 'guid',
      render(guid: string, record) {
        return (
          <BaseTableOptionsDropdown
            loading={loading && guid === updatedItemGuid}
            onEdit={() => {
              toggleOpen();
              setConditionForUpdate({
                code: record.icdCode.code,
                description: record.description,
                diagnoseGuid: record.icdCode.guid,
                patientGuid: record.patient.guid,
                guid,
              });
            }}
            onDelete={() => {
              setConditionForUpdate({ guid });
              handleDelete(guid);
            }}
            disabled={disableActions}
          />
        );
      },
    },
  ];
};
